// Takeoffs list page — /app/takeoffs
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, PencilRuler, FileText, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { nzd } from "@/lib/takeoffGeo";

const STATUS = {
  draft: { label: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-white/10 dark:text-zinc-300 dark:border-white/15" },
  in_progress: { label: "Measuring", cls: "bg-sky-100 text-sky-800 border-sky-400 dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-500/40" },
  complete: { label: "Complete", cls: "bg-emerald-100 text-emerald-800 border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/40" },
  exported: { label: "→ Quote", cls: "bg-violet-100 text-violet-800 border-violet-400 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/40" },
};

export default function Takeoffs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: "", client_id: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: cs }] = await Promise.all([api.get("/takeoffs"), api.get("/clients")]);
        setItems(data || []); setClients(cs || []);
      } catch { setItems([]); } finally { setLoading(false); }
    })();
    // Deep link from the global "+ Create" menu
    if (new URLSearchParams(window.location.search).get("new") === "1") setOpen(true);
  }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the takeoff a name");
    setCreating(true);
    try {
      const client = clients.find((c) => c.id === form.client_id);
      const { data } = await api.post("/takeoffs", {
        name: form.name.trim(), client_id: form.client_id || null, client_name: client?.name || null,
      });
      navigate(`/app/takeoffs/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create takeoff");
      setCreating(false);
    }
  };

  const del = async (e, tk) => {
    e.stopPropagation();
    if (!window.confirm(`Delete takeoff "${tk.name}"? Drawings and measurements will be lost.`)) return;
    try {
      await api.delete(`/takeoffs/${tk.id}`);
      setItems((p) => p.filter((x) => x.id !== tk.id));
      toast.success("Takeoff deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="takeoffs-page">
      <PageHeader
        eyebrow="Estimating"
        title="Takeoffs"
        subtitle="Measure quantities straight off your drawings, build up rates, and push them into a quote."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-xs" data-testid="takeoff-new-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Takeoff
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm sm:max-w-sm" data-testid="takeoff-new-dialog">
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                  <PencilRuler className="w-4 h-4 text-[#FF5F15]" /> New Takeoff
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Name</span>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Rata St subdivision — Stage 2" className="rounded-sm" autoFocus
                    onKeyDown={(e) => e.key === "Enter" && create()} data-testid="takeoff-new-name" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Customer (optional)</span>
                  <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    className="w-full h-9 rounded-sm text-sm bg-transparent border border-zinc-300 dark:border-white/15 px-2 dark:bg-[#0F141C]"
                    data-testid="takeoff-new-client">
                    <option value="">No customer yet</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <Button onClick={create} disabled={creating}
                  className="w-full rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-xs" data-testid="takeoff-new-create">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & Open Workspace"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>
      ) : !items.length ? (
        <div className="border-2 border-dashed border-zinc-300 dark:border-white/15 py-16 text-center">
          <PencilRuler className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
          <p className="text-sm font-bold uppercase tracking-widest text-zinc-500">No takeoffs yet</p>
          <p className="text-xs text-zinc-500 mt-1">Create one, upload a drawing PDF, and start measuring.</p>
        </div>
      ) : (
        <div className="border-2 border-zinc-300 dark:border-white/15 bg-white dark:bg-[#0F141C] divide-y divide-zinc-100 dark:divide-white/5">
          <div className="hidden md:grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px_110px_120px_100px_40px] gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 bg-zinc-50 dark:bg-white/[0.03]">
            <span>Takeoff</span><span>Customer</span><span>Drawings</span><span>Measurements</span><span className="text-right">Est. value</span><span>Status</span><span />
          </div>
          {items.map((tk) => {
            const st = STATUS[tk.status] || STATUS.draft;
            return (
              <div key={tk.id} onClick={() => navigate(`/app/takeoffs/${tk.id}`)}
                className="grid grid-cols-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px_110px_120px_100px_40px] gap-2 md:gap-3 px-4 py-3 items-center cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/[0.03]"
                data-testid={`takeoff-row-${tk.id}`}>
                <div className="min-w-0 col-span-2 md:col-span-1">
                  <div className="flex items-center gap-2 text-sm font-bold truncate"><PencilRuler className="w-3.5 h-3.5 text-[#FF5F15] shrink-0" /> {tk.name}</div>
                  <div className="text-[11px] text-zinc-500">Updated {tk.updated_at ? new Date(tk.updated_at).toLocaleDateString("en-NZ") : "—"}</div>
                </div>
                <span className="text-xs text-zinc-500 truncate">{tk.client_name || "—"}</span>
                <span className="text-xs font-mono"><FileText className="w-3 h-3 inline mr-1 text-zinc-400" />{tk.drawing_count}</span>
                <span className="text-xs font-mono">{tk.measurement_count}</span>
                <span className="text-xs font-mono font-bold md:text-right text-[#FF5F15]">{tk.est_value ? nzd(tk.est_value) : "—"}</span>
                <span className={`justify-self-start px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${st.cls}`}>{st.label}</span>
                <button onClick={(e) => del(e, tk)} className="text-zinc-400 hover:text-red-500 justify-self-end" data-testid={`takeoff-delete-${tk.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
